// MarkdownRenderer.tsx

import React from "react";
import { useTheme } from "@mui/material/styles";
import { styled, alpha } from "@mui/material";
import Markdown from "react-markdown";
import remarkGfm from "remark-gfm";
import remarkMath from "remark-math";
import rehypeKatex from "rehype-katex";
import "katex/dist/katex.min.css";

const StyledImage = styled("img")(({ theme }) => ({
  maxWidth: "100%",
  height: "auto",
  borderRadius: "8px",
  marginTop: theme.spacing(2),
  marginBottom: theme.spacing(2),
}));

const StyledListItem = styled("li")(({ theme }) => ({
  marginTop: theme.spacing(0.5),
}));

const StyledList = styled("ul")(({ theme }) => ({
  paddingLeft: theme.spacing(3),
  marginTop: theme.spacing(1),
  marginBottom: theme.spacing(1),
}));

const StyledStrong = styled("strong")(({ theme }) => ({
  fontWeight: 600,
}));

const MarkdownRenderer: React.FC<{ content: string }> = ({ content }) => {
  const theme = useTheme();

  const imageRegex = /!\[(.*?)\]\((data:image\/png;base64,.*?)\)/;
  const match = content.match(imageRegex);

  let imageUrl = null;
  let textContent = content;
  let imageAlt = "Generated Infographic";

  if (match) {
    imageAlt = match[1] || imageAlt;
    imageUrl = match[2];
    textContent = content.replace(imageRegex, "").trim();
  }

  const CodeBlock = React.memo(({ inline, className, children }: any) => {
    const code = String(children).replace(/\n$/, "");
    if (!inline) {
      return (
        <pre
          style={{
            backgroundColor: alpha(theme.palette.common.black, 0.25),
            padding: theme.spacing(1.5),
            borderRadius: "8px",
            overflowX: "auto",
            margin: theme.spacing(1, 0),
            color: '#f8f8f2',
            fontFamily: "monospace",
            fontSize: "0.875rem",
          }}
        >
          <code className={className}>{code}</code>
        </pre>
      );
    }
    return (
      <code
        style={{
          backgroundColor: alpha(theme.palette.text.primary, 0.1),
          padding: "2px 6px",
          borderRadius: "4px",
          fontSize: "0.875em",
          fontFamily: "monospace",
        }}
      >
        {children}
      </code>
    );
  });

  const components = {
    code: CodeBlock as any,
    ul: ({ children, ...props }: any) => <StyledList {...props}>{children}</StyledList>,
    ol: ({ children, ...props }: any) => <StyledList as="ol" {...props}>{children}</StyledList>,
    li: ({ children, ...props }: any) => <StyledListItem {...props}>{children}</StyledListItem>,
    strong: ({ children, ...props }: any) => <StyledStrong {...props}>{children}</StyledStrong>,
  };

  return (
    <>
      <Markdown
        remarkPlugins={[remarkGfm, remarkMath]}
        components={components}
        rehypePlugins={[rehypeKatex]}
      >
        {textContent}
      </Markdown>
      {imageUrl && <StyledImage src={imageUrl} alt={imageAlt} />}
    </>
  );
};

export default MarkdownRenderer;